import React, { useState, useEffect, useCallback } from 'react';
import {
    FiX, FiUsers, FiSettings, FiMessageSquare,
    FiCopy, FiCheck, FiAlertTriangle, FiShield,
    FiToggleLeft, FiToggleRight, FiLink, FiLock, FiInfo
} from 'react-icons/fi';
import { apiFetch } from '../utils/api';

const PLANS = ['free', 'starter', 'team', 'business', 'enterprise'];
const PLAN_COLORS = {
    free:       { bg: 'bg-gray-700',        text: 'text-gray-300' },
    starter:    { bg: 'bg-blue-900/50',     text: 'text-blue-300' },
    team:       { bg: 'bg-purple-900/50',   text: 'text-purple-300' },
    business:   { bg: 'bg-amber-900/50',    text: 'text-amber-300' },
    enterprise: { bg: 'bg-emerald-900/50',  text: 'text-emerald-300' },
};
const PlanBadge = ({ plan }) => {
    const c = PLAN_COLORS[plan] || { bg: 'bg-gray-700', text: 'text-gray-300' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{plan}</span>;
};
const RoleBadge = ({ role }) => {
    const c = role === 'owner' ? 'bg-amber-900/50 text-amber-300' :
              role === 'admin' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-700 text-gray-400';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c}`}>{role}</span>;
};

const PRIORITY_COLORS = {
    critique: 'text-red-400',
    haute:    'text-orange-400',
    moyenne:  'text-yellow-400',
    basse:    'text-blue-400',
    minimale: 'text-gray-400',
};

const UsageBar = ({ label, used, max }) => {
    const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
    const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">{label}</span>
                <span className={pct >= 90 ? 'text-red-400' : 'text-gray-400'}>{used} / {max}</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const TABS = [
    { id: 'overview',  label: 'Overview',  icon: FiInfo },
    { id: 'members',   label: 'Membres',   icon: FiUsers },
    { id: 'messages',  label: 'Messages',  icon: FiMessageSquare },
    { id: 'config',    label: 'Config',    icon: FiSettings },
];

const OrgDetailDrawer = ({ orgId, onClose, onUpdate }) => {
    const [tab,          setTab]          = useState('overview');
    const [org,          setOrg]          = useState(null);
    const [messages,     setMessages]     = useState(null);
    const [msgPage,      setMsgPage]      = useState(1);
    const [loading,      setLoading]      = useState(true);
    const [msgLoading,   setMsgLoading]   = useState(false);
    const [actionLoading,setActionLoading]= useState(null);
    const [copied,       setCopied]       = useState(false);
    const [notification, setNotification] = useState(null);

    const notify = (msg, ok = true) => {
        setNotification({ msg, ok });
        setTimeout(() => setNotification(null), 3000);
    };

    const loadOrg = useCallback(async () => {
        setLoading(true);
        try {
            const d = await apiFetch(`/superadmin/orgs/${orgId}`);
            setOrg(d);
        } catch (e) { notify(e.message, false); }
        finally { setLoading(false); }
    }, [orgId]);

    const loadMessages = useCallback(async (page = 1) => {
        setMsgLoading(true);
        try {
            const d = await apiFetch(`/superadmin/orgs/${orgId}/messages?page=${page}&limit=20`);
            setMessages(d);
            setMsgPage(page);
        } catch (e) { notify(e.message, false); }
        finally { setMsgLoading(false); }
    }, [orgId]);

    useEffect(() => { loadOrg(); }, [loadOrg]);
    useEffect(() => { if (tab === 'messages' && !messages) loadMessages(1); }, [tab, messages, loadMessages]);

    const handleStatus = async () => {
        setActionLoading('status');
        try {
            const newStatus = org.status === 'active' ? 'suspended' : 'active';
            await apiFetch(`/superadmin/orgs/${orgId}/status`, {
                method: 'PATCH', body: JSON.stringify({ status: newStatus })
            });
            setOrg(o => ({ ...o, status: newStatus }));
            notify(`Organisation ${newStatus === 'active' ? 'réactivée' : 'suspendue'}`);
            onUpdate?.();
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    const handlePlan = async (newPlan) => {
        setActionLoading('plan');
        try {
            await apiFetch(`/superadmin/orgs/${orgId}/plan`, {
                method: 'PATCH', body: JSON.stringify({ plan: newPlan })
            });
            setOrg(o => ({ ...o, plan: newPlan }));
            notify(`Plan mis à jour : ${newPlan}`);
            onUpdate?.();
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    const handleJoin = async () => {
        setActionLoading('join');
        try {
            await apiFetch(`/superadmin/orgs/${orgId}/join`, { method: 'POST' });
            notify('Vous avez rejoint l\'organisation');
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    const copyInviteLink = () => {
        if (!org?.inviteCode) return;
        const url = `${window.location.origin.replace(':3001', ':3000')}/join/${org.inviteCode}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Fermer avec Escape
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/60 z-[999]" onClick={onClose} />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full sm:w-[700px] bg-gray-950 border-l border-gray-800 flex flex-col shadow-2xl z-[9999] animate-slideIn overflow-hidden">
                {/* Notification */}
                {notification && (
                    <div className={`absolute top-4 right-4 z-10 px-4 py-2 rounded-lg text-sm font-medium ${
                        notification.ok ? 'bg-emerald-900/90 text-emerald-200' : 'bg-red-900/90 text-red-200'
                    }`}>
                        {notification.msg}
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0">
                    {org ? (
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${org.status === 'active' ? 'bg-emerald-400' : 'bg-red-500'}`} />
                            <div className="min-w-0">
                                <h2 className="text-white font-bold text-lg truncate">{org.name}</h2>
                                <p className="text-gray-500 text-sm">/{org.slug}</p>
                            </div>
                            <PlanBadge plan={org.plan} />
                        </div>
                    ) : (
                        <div className="h-6 w-48 bg-gray-800 rounded animate-pulse" />
                    )}
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-white transition-colors shrink-0">
                        <FiX size={18} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-800 px-6 shrink-0">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)}
                                className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    tab === t.id
                                        ? 'border-brand-500 text-white'
                                        : 'border-transparent text-gray-500 hover:text-gray-300'
                                }`}>
                                <Icon size={13} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : org && (
                        <>
                            {/* ── OVERVIEW ── */}
                            {tab === 'overview' && (
                                <div className="space-y-5">
                                    {/* Infos de base */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {[
                                            ['ID', org._id],
                                            ['Type', org.type],
                                            ['Statut', org.status],
                                            ['Créé le', new Date(org.createdAt).toLocaleDateString('fr-FR')],
                                            ['Modifié', org.updatedAt ? new Date(org.updatedAt).toLocaleDateString('fr-FR') : '—'],
                                            ['Membres', org.memberCount],
                                        ].map(([k, v]) => (
                                            <div key={k} className="bg-gray-900 rounded-lg p-3">
                                                <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                <div className="text-white font-mono text-xs break-all">{String(v)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Usage vs Limits */}
                                    <div className="bg-gray-900 rounded-xl p-4 space-y-3">
                                        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Utilisation vs Limites</h3>
                                        <UsageBar label="Utilisateurs" used={org.usage?.users ?? 0} max={org.limits?.maxUsers ?? 5} />
                                        <UsageBar label="Messages" used={org.usage?.messages ?? 0} max={org.limits?.maxMessages ?? 100} />
                                        <UsageBar label="Todos" used={org.usage?.todos ?? 0} max={org.limits?.maxTodos ?? 50} />
                                    </div>

                                    {/* Lien d'invitation */}
                                    <div className="bg-gray-900 rounded-xl p-4">
                                        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <FiLink size={12} /> Lien d'invitation
                                        </h3>
                                        {org.inviteCode ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
                                                    <code className="text-green-400 text-xs font-mono flex-1 break-all">
                                                        {org.inviteCode}
                                                    </code>
                                                    <button onClick={copyInviteLink}
                                                        className="shrink-0 p-1 rounded text-gray-400 hover:text-white transition-colors"
                                                        title="Copier le lien">
                                                        {copied ? <FiCheck size={14} className="text-green-400" /> : <FiCopy size={14} />}
                                                    </button>
                                                </div>
                                                {org.inviteCodeExpiresAt && (
                                                    <p className="text-gray-600 text-xs">
                                                        Expire le {new Date(org.inviteCodeExpiresAt).toLocaleDateString('fr-FR')}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-gray-600 text-sm">Aucun code d'invitation actif</p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="bg-gray-900 rounded-xl p-4">
                                        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Actions rapides</h3>
                                        <div className="flex flex-wrap gap-3">
                                            {/* Changer plan */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-400 text-xs">Plan :</span>
                                                <select
                                                    value={org.plan}
                                                    onChange={e => handlePlan(e.target.value)}
                                                    disabled={actionLoading === 'plan'}
                                                    className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-gray-300 text-xs focus:outline-none disabled:opacity-50"
                                                >
                                                    {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                                                </select>
                                            </div>
                                            {/* Suspendre/Réactiver */}
                                            <button onClick={handleStatus} disabled={actionLoading === 'status'}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                                                    org.status === 'active'
                                                        ? 'border-red-800 text-red-400 hover:bg-red-900/30'
                                                        : 'border-emerald-800 text-emerald-400 hover:bg-emerald-900/30'
                                                }`}>
                                                {org.status === 'active' ? <FiToggleLeft size={14} /> : <FiToggleRight size={14} />}
                                                {org.status === 'active' ? 'Suspendre' : 'Réactiver'}
                                            </button>
                                            {/* Rejoindre (bypass limit) */}
                                            <button onClick={handleJoin} disabled={actionLoading === 'join'}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-brand-700 text-brand-400 hover:bg-brand-900/30 transition-colors disabled:opacity-50">
                                                <FiShield size={14} />
                                                {actionLoading === 'join' ? 'Rejoindre...' : 'Rejoindre (bypass)'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── MEMBRES ── */}
                            {tab === 'members' && (
                                <div className="space-y-3">
                                    <p className="text-gray-500 text-sm">{org.memberCount} membre{org.memberCount > 1 ? 's' : ''}</p>
                                    {org.members?.length === 0 ? (
                                        <p className="text-gray-600 text-sm">Aucun membre</p>
                                    ) : (
                                        <div className="bg-gray-900 rounded-xl overflow-hidden">
                                            {org.members.map((m, i) => (
                                                <div key={m._id} className={`px-4 py-3 ${i > 0 ? 'border-t border-gray-800' : ''}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-8 h-8 rounded-full bg-brand-600/20 flex items-center justify-center text-brand-400 text-xs font-bold shrink-0">
                                                                {(m.name || '?')[0].toUpperCase()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-white text-sm font-medium">{m.name}</span>
                                                                    {!m.isEmailVerified && <FiAlertTriangle size={12} className="text-amber-400" title="Email non vérifié" />}
                                                                    {m.twoFactorEnabled && <FiLock size={12} className="text-emerald-400" title="2FA activé" />}
                                                                </div>
                                                                <span className="text-gray-500 text-xs">{m.email}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <RoleBadge role={m.role} />
                                                        </div>
                                                    </div>
                                                    <div className="mt-1.5 flex gap-4 text-xs text-gray-600 pl-11">
                                                        <span>Rejoint : {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString('fr-FR') : '—'}</span>
                                                        <span>Auth : {m.authProvider}</span>
                                                        {(m.loginAttempts ?? 0) >= 3 && (
                                                            <span className="text-amber-400">{m.loginAttempts} tentatives</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── MESSAGES ── */}
                            {tab === 'messages' && (
                                <div className="space-y-3">
                                    {msgLoading ? (
                                        <div className="flex justify-center py-10">
                                            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    ) : messages ? (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-500 text-sm">{messages.total} message{messages.total > 1 ? 's' : ''}</p>
                                                {messages.pages > 1 && (
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => loadMessages(msgPage - 1)} disabled={msgPage <= 1}
                                                            className="px-2 py-1 text-xs rounded border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30">←</button>
                                                        <span className="text-gray-500 text-xs">{msgPage}/{messages.pages}</span>
                                                        <button onClick={() => loadMessages(msgPage + 1)} disabled={msgPage >= messages.pages}
                                                            className="px-2 py-1 text-xs rounded border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30">→</button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                {messages.messages.map(msg => (
                                                    <div key={msg._id} className="bg-gray-900 rounded-xl p-4">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                    <span className="text-white text-sm font-semibold truncate">{msg.title}</span>
                                                                    <span className={`text-xs font-medium ${PRIORITY_COLORS[msg.priority] || 'text-gray-400'}`}>
                                                                        {msg.priority}
                                                                    </span>
                                                                </div>
                                                                <p className="text-gray-400 text-xs line-clamp-2">{msg.content}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                                                            <span>{msg.type}</span>
                                                            <span>{msg.role}</span>
                                                            <span>Par {msg.createdByName}</span>
                                                            <span className="ml-auto">{new Date(msg.createdAt).toLocaleDateString('fr-FR')}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                                {messages.messages.length === 0 && (
                                                    <p className="text-gray-600 text-sm text-center py-8">Aucun message</p>
                                                )}
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            )}

                            {/* ── CONFIG ── */}
                            {tab === 'config' && (
                                <div className="space-y-5">
                                    {/* Branding */}
                                    {org.branding && (
                                        <div className="bg-gray-900 rounded-xl p-4">
                                            <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Branding</h3>
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    ['Nom', org.branding.name || '—'],
                                                    ['Police', org.branding.font || 'Inter'],
                                                    ['Couleur', org.branding.color || '—'],
                                                    ['Taille', org.branding.size || '1.25'],
                                                ].map(([k, v]) => (
                                                    <div key={k} className="bg-gray-800 rounded-lg p-3">
                                                        <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                        <div className="flex items-center gap-2">
                                                            {k === 'Couleur' && v !== '—' && (
                                                                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: v }} />
                                                            )}
                                                            <span className="text-white font-mono text-xs">{String(v)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Message Types */}
                                    {org.messageTypes?.length > 0 && (
                                        <div className="bg-gray-900 rounded-xl p-4">
                                            <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Types de messages</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {org.messageTypes.map((t, i) => (
                                                    <span key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-800"
                                                        style={{ color: t.color }}>
                                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.color }} />
                                                        {t.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Message Roles */}
                                    {org.messageRoles?.length > 0 && (
                                        <div className="bg-gray-900 rounded-xl p-4">
                                            <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Rôles de messages</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {org.messageRoles.map((r, i) => (
                                                    <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-800 text-gray-300">{r}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Settings (map) */}
                                    {org.settings && Object.keys(org.settings).length > 0 && (
                                        <div className="bg-gray-900 rounded-xl p-4">
                                            <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Settings</h3>
                                            <div className="space-y-2">
                                                {Object.entries(org.settings).map(([k, v]) => (
                                                    <div key={k} className="flex justify-between items-center bg-gray-800 rounded-lg px-3 py-2">
                                                        <span className="text-gray-400 text-xs font-mono">{k}</span>
                                                        <span className="text-white text-xs font-mono break-all max-w-[60%] text-right">{String(v)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Limites */}
                                    <div className="bg-gray-900 rounded-xl p-4">
                                        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Limites du plan</h3>
                                        <div className="grid grid-cols-3 gap-3">
                                            {[
                                                ['Max Utilisateurs', org.limits?.maxUsers],
                                                ['Max Messages', org.limits?.maxMessages],
                                                ['Max Todos', org.limits?.maxTodos],
                                            ].map(([k, v]) => (
                                                <div key={k} className="bg-gray-800 rounded-lg p-3 text-center">
                                                    <div className="text-white text-xl font-bold">{v ?? '—'}</div>
                                                    <div className="text-gray-500 text-xs mt-1">{k}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default OrgDetailDrawer;
